import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { UserDevicesResponse } from '../../services/regularUserService';
import { PLATFORM_LABEL_FA } from './UserPlatformBadge';

/**
 * دستگاه‌ها و سشن‌های یک کاربر.
 *
 * پاسخِ ریزدانه به «این کاربر با چه چیزی کار می‌کند»: هر ردیفِ دستگاه یک
 * «نصب» است (نه یک گوشی)، چون کلیدش شناسهٔ نصبِ اپ است و با حذف/نصب دوباره
 * عوض می‌شود. برای وب، هر مرورگر/پروفایل یک ردیف می‌شود.
 */

const fmtDateTime = (d?: string) => {
  if (!d) return '-';
  const date = new Date(d);
  return `${date.toLocaleDateString('fa-IR')} - ${date.toLocaleTimeString('fa-IR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const fmtDuration = (sec?: number) => {
  if (!sec || sec <= 0) return '-';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}د ${s}ث` : `${s}ث`;
};

const INSTALL_SOURCE_FA: Record<string, string> = {
  cafebazaar: 'کافه‌بازار',
  myket: 'مایکت',
  googleplay: 'گوگل‌پلی',
  appstore: 'اپ‌استور',
  direct: 'نصب مستقیم',
};

const sectionTitle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  margin: '18px 0 8px',
};

const table: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
};

const th: React.CSSProperties = {
  textAlign: 'right',
  padding: '6px 8px',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
  color: '#6b7280',
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  textAlign: 'right',
  padding: '6px 8px',
  borderBottom: '1px solid rgba(0,0,0,0.05)',
};

interface Props {
  open: boolean;
  onClose: () => void;
  userName?: string;
  loading: boolean;
  data: UserDevicesResponse | null;
}

const UserDevicesDialog: React.FC<Props> = ({ open, onClose, userName, loading, data }) => {
  const usage = data?.usage;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir="rtl">
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <span>دستگاه‌ها و فعالیت {userName ? `— ${userName}` : ''}</span>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <CircularProgress size={32} />
          </div>
        )}

        {!loading && data && (
          <>
            {/* خلاصه */}
            <div style={sectionTitle}>خلاصه</div>
            {usage ? (
              <table style={table}>
                <tbody>
                  <tr>
                    <td style={td}>کانال اصلی</td>
                    <td style={td}>
                      {usage.primaryChannel === 'app'
                        ? 'اپلیکیشن'
                        : usage.primaryChannel === 'web'
                        ? 'وب‌سایت'
                        : usage.primaryChannel === 'both'
                        ? 'اپ + وب'
                        : 'نامشخص'}
                    </td>
                    <td style={td}>آخرین فعالیت</td>
                    <td style={td}>{fmtDateTime(usage.lastActiveAt)}</td>
                  </tr>
                  <tr>
                    <td style={td}>سشن‌های اپ</td>
                    <td style={td}>{usage.appSessions ?? 0}</td>
                    <td style={td}>سشن‌های وب</td>
                    <td style={td}>{usage.webSessions ?? 0}</td>
                  </tr>
                  <tr>
                    <td style={td}>اولین استفاده از اپ</td>
                    <td style={td}>{fmtDateTime(usage.firstAppAt)}</td>
                    <td style={td}>نسخهٔ اپ</td>
                    <td style={td}>
                      {usage.appVersion || '-'}
                      {usage.appBuild ? ` (${usage.appBuild})` : ''}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div style={{ color: '#6b7280', fontSize: 12 }}>
                هنوز فعالیتی برای این کاربر ثبت نشده است.
              </div>
            )}

            {/* دستگاه‌ها */}
            <div style={sectionTitle}>دستگاه‌ها ({data.devices.length})</div>
            {data.devices.length === 0 ? (
              <div style={{ color: '#6b7280', fontSize: 12 }}>دستگاهی ثبت نشده است.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}>پلتفرم</th>
                      <th style={th}>دستگاه</th>
                      <th style={th}>سیستم‌عامل</th>
                      <th style={th}>نسخهٔ اپ</th>
                      <th style={th}>منبع نصب</th>
                      <th style={th}>سشن</th>
                      <th style={th}>اولین بار</th>
                      <th style={th}>آخرین بار</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.devices.map((d) => (
                      <tr key={d._id}>
                        <td style={td}>{PLATFORM_LABEL_FA[d.platform] || d.platform}</td>
                        <td style={td}>
                          {[d.deviceBrand, d.deviceModel].filter(Boolean).join(' ') ||
                            d.browser ||
                            '-'}
                        </td>
                        <td style={td}>
                          {[d.osName, d.osVersion].filter(Boolean).join(' ') || '-'}
                        </td>
                        <td style={td}>{d.appVersion || '-'}</td>
                        <td style={td}>
                          {d.installSource
                            ? INSTALL_SOURCE_FA[d.installSource] || d.installSource
                            : '-'}
                        </td>
                        <td style={td}>{d.sessionCount}</td>
                        <td style={td}>{fmtDateTime(d.firstSeenAt)}</td>
                        <td style={td}>{fmtDateTime(d.lastSeenAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* سشن‌های اخیر */}
            <div style={sectionTitle}>سشن‌های اخیر ({data.sessions.length})</div>
            {data.sessions.length === 0 ? (
              <div style={{ color: '#6b7280', fontSize: 12 }}>سشنی ثبت نشده است.</div>
            ) : (
              <div style={{ overflowX: 'auto', maxHeight: 320 }}>
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}>شروع</th>
                      <th style={th}>پلتفرم</th>
                      <th style={th}>مدت</th>
                      <th style={th}>صفحه</th>
                      <th style={th}>رویداد</th>
                      <th style={th}>دستگاه</th>
                      <th style={th}>شهر</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sessions.map((s) => (
                      <tr key={s.sessionId}>
                        <td style={td}>{fmtDateTime(s.startedAt)}</td>
                        <td style={td}>{PLATFORM_LABEL_FA[s.platform] || s.platform}</td>
                        <td style={td}>{fmtDuration(s.durationSec)}</td>
                        <td style={td}>{s.pageViewCount}</td>
                        <td style={td}>{s.eventCount}</td>
                        <td style={td}>
                          {s.app?.deviceModel ||
                            [s.device?.os, s.device?.browser].filter(Boolean).join(' / ') ||
                            '-'}
                        </td>
                        <td style={td}>{s.city || s.country || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserDevicesDialog;
